import { MoneyRoundingNote, MoneyValue } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { formatPercentageLabel } from '@shared/lib/format/percent'
import type { LedgerItem, SaleItem } from '../api/admin.api'
import {
  isCurrencyFeeLedgerEntry,
  isVendepaySaleFeeLedgerEntry,
} from '../lib/admin-sale-detail-money'
import { sumLedgerByCurrency, reportingAmountsOf } from '../lib/admin-money'
import type { MoneySummary } from '../lib/admin-money'
import { saleFinancialImpact } from '../lib/admin-sale-events'
import {
  muted,
  saleLine,
  saleLineHint,
  saleLineLabel,
  saleLineValue,
  saleSection,
  saleSectionTitle,
  saleSoftNote,
} from './admin.css'

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function MoneySummaryList({ rows, empty }: { rows: MoneySummary[]; empty: string }) {
  if (rows.length === 0) {
    return <span className={muted}>{empty}</span>
  }
  return (
    <>
      {rows.map((row) => (
        <MoneyValue amount={row.amount} currency={row.currency} key={row.currency} size="sm" />
      ))}
    </>
  )
}

function chargedAmountPresentation(status: SaleItem['status']) {
  if (status === 'FAILED') {
    return {
      label: 'Valor tentado',
      hint: 'Cobrança não concluída pelo gateway',
    }
  }
  if (status === 'PENDING') {
    return {
      label: 'Valor aguardando confirmação',
      hint: 'Pagamento ainda não confirmado pelo gateway',
    }
  }
  return {
    label: 'Total cobrado',
    hint: 'Moeda paga pelo comprador',
  }
}

type AdminSaleFinancialSummaryProps = {
  sale: SaleItem
  ledgerEntries: LedgerItem[]
  ledgerLoading: boolean
  ledgerError: boolean
}

export function AdminSaleFinancialSummary({
  sale,
  ledgerEntries,
  ledgerLoading,
  ledgerError,
}: AdminSaleFinancialSummaryProps) {
  const customServiceFees = useFeatureFlag('customServiceFees')
  const charged = chargedAmountPresentation(sale.status)
  const impact = saleFinancialImpact(sale)
  const reportingSnapshots = reportingAmountsOf(sale)
  const showConversionSnapshotNote = sale.amountPaid.currency !== 'BRL'

  return (
    <div className={saleSection} data-testid="admin-sale-financial-summary">
      <span className={saleSectionTitle}>Resumo financeiro</span>
      {sale.serviceFeeQuote ? (
        <>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Serviço VendePay
              <span className={saleLineHint}>
                {customServiceFees
                  ? formatPercentageLabel(sale.serviceFeeQuote.percentage)
                  : `${sale.serviceFeeQuote.percentage.replace('.', ',')}%`}{' '}
                do bruto comercial + US$ {sale.serviceFeeQuote.sourceAmount.replace('.', ',')} por
                cobrança. Sem taxa de conversão.
              </span>
            </span>
            <span className={saleLineValue}>
              <MoneySummaryList
                rows={sumLedgerByCurrency(
                  ledgerEntries.filter(
                    (entry) =>
                      isVendepaySaleFeeLedgerEntry(entry) || isCurrencyFeeLedgerEntry(entry),
                  ),
                )}
                empty={ledgerLoading ? 'Conferindo...' : 'Serviço não informado'}
              />
            </span>
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Tarifa fixa convertida
              <span className={saleLineHint}>
                1 USD = {sale.serviceFeeQuote.rate} {sale.serviceFeeQuote.currency}
              </span>
            </span>
            <MoneyValue
              amount={sale.serviceFeeQuote.fixedAmount}
              currency={sale.serviceFeeQuote.currency}
              size="sm"
            />
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Cotação registrada
              <span className={saleLineHint}>
                {sale.serviceFeeQuote.providerQuoteId ?? sale.serviceFeeQuote.rateKind}
              </span>
            </span>
            <span className={saleLineValue}>{formatDateTime(sale.serviceFeeQuote.quotedAt)}</span>
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>Validade original da cotação</span>
            <span className={saleLineValue}>{formatDateTime(sale.serviceFeeQuote.expiresAt)}</span>
          </div>
        </>
      ) : null}
      <div className={saleLine}>
        <span className={saleLineLabel}>
          {impact.hasImpact ? 'Total cobrado' : charged.label}
          <span className={saleLineHint}>
            {impact.hasImpact ? 'Total confirmado antes do acontecimento' : charged.hint}
          </span>
        </span>
        <span className={saleLineValue}>
          <MoneyValue
            amount={impact.hasImpact ? impact.original.amount : sale.amountPaid.amount}
            currency={impact.hasImpact ? impact.original.currency : sale.amountPaid.currency}
            size="sm"
          />
        </span>
      </div>
      {impact.hasImpact ? (
        <>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Continua válido
              <span className={saleLineHint}>
                Parte da cobrança que não foi reembolsada nem contestada
              </span>
            </span>
            <span className={saleLineValue}>
              <MoneyValue
                amount={impact.active.amount}
                currency={impact.active.currency}
                size="sm"
              />
            </span>
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Reembolsado ou contestado
              <span className={saleLineHint}>Parte retirada do valor válido da cobrança</span>
            </span>
            <span className={saleLineValue}>
              <MoneyValue
                amount={impact.affected.amount}
                currency={impact.affected.currency}
                size="sm"
              />
            </span>
          </div>
        </>
      ) : null}
      {sale.commercialAmount && sale.fxVariation ? (
        <>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Preço contratado
              <span className={saleLineHint}>Valor da compra em reais, após descontos</span>
            </span>
            <span className={saleLineValue}>
              <MoneyValue {...sale.commercialAmount} size="sm" />
            </span>
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>
              Variação cambial
              <span className={saleLineHint}>Diferença para o bruto comercial desta cobrança</span>
            </span>
            <span className={saleLineValue}>
              <MoneyValue {...sale.fxVariation} size="sm" />
            </span>
          </div>
          <MoneyRoundingNote
            total={sale.grossAmount}
            parts={[sale.commercialAmount, sale.fxVariation]}
          />
        </>
      ) : null}
      <div className={saleLine}>
        <span className={saleLineLabel}>
          {sale.buyerPaidConversion
            ? 'Bruto comercial'
            : ledgerEntries.length > 0
              ? 'Total liquidado'
              : 'Liquidação'}
          <span className={saleLineHint}>
            {sale.buyerPaidConversion
              ? 'Base das taxas, reserva e divisão entre participantes'
              : ledgerEntries.length > 0
                ? 'Valor usado no ledger da cobrança'
                : 'Situação dos lançamentos financeiros'}
          </span>
        </span>
        <span className={saleLineValue}>
          {ledgerEntries.length > 0 ? (
            <MoneyValue
              amount={sale.grossAmount.amount}
              currency={sale.grossAmount.currency}
              size="sm"
            />
          ) : ledgerLoading ? (
            <span className={muted}>Conferindo ledger...</span>
          ) : ledgerError ? (
            <span className={muted}>Ledger indisponível</span>
          ) : (
            <span className={muted}>Sem liquidação</span>
          )}
        </span>
      </div>
      {ledgerEntries.length === 0 && !ledgerLoading && !ledgerError ? (
        <div className={saleSoftNote}>
          <span>Nenhum valor foi lançado para participantes, taxas ou reserva.</span>
        </div>
      ) : null}
      {sale.buyerPaidConversion && sale.providerSettlementAmount ? (
        <div className={saleLine}>
          <span className={saleLineLabel}>
            Bruto recebido da Stripe
            <span className={saleLineHint}>
              Inclui a conversão paga pelo comprador, antes das tarifas do provedor
            </span>
          </span>
          <span className={saleLineValue}>
            <MoneyValue
              amount={sale.providerSettlementAmount.amount}
              currency={sale.providerSettlementAmount.currency}
              size="sm"
            />
          </span>
        </div>
      ) : null}
      {sale.settlementAmount && ledgerEntries.length > 0 && !sale.buyerPaidConversion ? (
        <div className={saleLine}>
          <span className={saleLineLabel}>Settlement informado</span>
          <span className={saleLineValue}>
            <MoneyValue
              amount={sale.settlementAmount.amount}
              currency={sale.settlementAmount.currency}
              size="sm"
            />
          </span>
        </div>
      ) : null}
      {sale.exchangeRate ? (
        <div className={saleSoftNote}>
          <span>
            {sale.buyerPaidConversion
              ? 'Conversão incluída no preço pago pelo comprador'
              : `Câmbio: ${sale.exchangeRate}${sale.exchangeRateKind ? ` · ${sale.exchangeRateKind}` : ''}${sale.exchangeRateSource ? ` · fonte ${sale.exchangeRateSource}` : ''}`}
          </span>
          {sale.exchangeRateQuotedAt ? (
            <span>Cotado em {formatDateTime(sale.exchangeRateQuotedAt)}</span>
          ) : null}
        </div>
      ) : null}
      {reportingSnapshots.length > 0 ? (
        <div className={saleLine}>
          <span className={saleLineLabel}>
            Snapshot gerencial
            <span className={saleLineHint}>Indicadores BRL/USD; não altera o ledger real</span>
          </span>
          <span className={saleLineValue}>
            <MoneySummaryList rows={reportingSnapshots} empty="Sem snapshot gerencial." />
          </span>
        </div>
      ) : null}
      {showConversionSnapshotNote ? (
        <div className={saleSoftNote}>
          <span>
            Este snapshot serve à auditoria comercial. Não confirma conversão efetiva nem pagamento
            bancário.
          </span>
        </div>
      ) : null}
    </div>
  )
}
